// 설비 현황 관련 유틸리티 함수
import {
  MACHINE_META,
  MACHINE_KEYS,
  EquipmentCategory,
  type MachineKey,
  type MachineMeta,
} from "../constants/machineKeys";

/**
 * 점검수량 계산
 * checkCount = ceil(totalCount × ratio)
 *
 * [Codex-002] 엣지 케이스 처리:
 * - NaN, Infinity 처리
 * - ratio 범위 제한 (0~1)
 * - 결과값 제한 (checkCount <= totalCount)
 *
 * @param totalCount 보유대수
 * @param ratio 점검 비율 (0.0 ~ 1.0)
 * @returns 점검수량 (올림 적용)
 */
export function calculateCheckCount(totalCount: number, ratio: number): number {
  // NaN, Infinity 처리
  if (!Number.isFinite(totalCount) || !Number.isFinite(ratio)) {
    return 0;
  }

  // 0 이하 처리
  if (totalCount <= 0 || ratio <= 0) {
    return 0;
  }

  // ratio 범위 제한 (0~1)
  const clampedRatio = Math.min(Math.max(ratio, 0), 1);

  // 점검수량 계산 (올림)
  const result = Math.ceil(totalCount * clampedRatio);

  // 결과값 제한 (checkCount <= totalCount)
  return Math.min(result, totalCount);
}

/**
 * 설비 키로 메타데이터 조회
 *
 * @param key 설비 타입 키
 * @returns 설비 메타데이터 (ratio, unit, category)
 */
export function getEquipmentMeta(key: MachineKey): MachineMeta {
  return MACHINE_META[key];
}

/**
 * 설비 키 유효성 검사
 * [Codex-001] equipmentKey 유효성 검증
 *
 * @param key 검사할 키
 * @returns 유효한 MachineKey인지 여부
 */
export function isValidEquipmentKey(key: string): key is MachineKey {
  return MACHINE_KEYS.includes(key as MachineKey);
}

/**
 * 카테고리 enum → 한글 레이블 매핑
 */
const CATEGORY_LABELS: Record<EquipmentCategory, string> = {
  [EquipmentCategory.COMMUNICATION]: "통신설비",
  [EquipmentCategory.BROADCAST]: "방송설비",
  [EquipmentCategory.INFORMATION]: "정보설비",
  [EquipmentCategory.OTHER]: "기타설비",
};

/**
 * 카테고리 순서 (보고서 출력용)
 */
export const EQUIPMENT_CATEGORY_ORDER = [
  EquipmentCategory.COMMUNICATION,
  EquipmentCategory.BROADCAST,
  EquipmentCategory.INFORMATION,
  EquipmentCategory.OTHER,
] as const;

/**
 * 카테고리 한글 레이블 조회
 *
 * @param category 카테고리 enum
 * @returns 한글 레이블 (예: "통신설비")
 */
export function getEquipmentCategoryLabel(category: EquipmentCategory): string {
  return CATEGORY_LABELS[category];
}

/**
 * 설비 키로 카테고리 한글 레이블 조회
 *
 * @param key 설비 키
 * @returns 카테고리 한글 레이블 (기본값: "기타설비")
 */
export function getEquipmentCategory(key: string): string {
  if (!isValidEquipmentKey(key)) {
    return CATEGORY_LABELS[EquipmentCategory.OTHER];
  }
  const meta = MACHINE_META[key];
  return CATEGORY_LABELS[meta.category];
}

/**
 * 카테고리별로 설비를 그룹화합니다.
 * 클라이언트/서버 양쪽에서 사용 가능한 제네릭 함수
 *
 * @param equipments 설비 배열 (key 필드 필수)
 * @param equipmentTypes 설비 타입 배열 (key, category 필드 필수)
 * @returns 카테고리별 그룹화된 설비 Record
 */
export function groupEquipmentsByCategory<
  T extends { key: string },
  U extends { key: string; category: EquipmentCategory },
>(equipments: T[], equipmentTypes: U[]): Record<EquipmentCategory, T[]> {
  // 설비 키 → 카테고리 맵 생성
  const categoryMap = new Map<string, EquipmentCategory>(
    equipmentTypes.map((et) => [et.key, et.category])
  );

  // 카테고리별로 그룹화
  const grouped: Record<EquipmentCategory, T[]> = {
    COMMUNICATION: [],
    BROADCAST: [],
    INFORMATION: [],
    OTHER: [],
  };

  for (const equipment of equipments) {
    const category = categoryMap.get(equipment.key) || EquipmentCategory.OTHER;
    grouped[category].push(equipment);
  }

  return grouped;
}
